import React, { useEffect, useRef, useState } from "react";
import { useLevelProgress } from "../stores/useLevelProgress";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import MenuSky from "./mainMenu/MenuSky";
import MapIslands from "./mainMenu/MapIslands";
import OceanSphere from "./mainMenu/OceanSphere";
import RotatablePlanet from "./mainMenu/RotatablePlanet";
import ConnectionLines from "./mainMenu/ConnectionLines";
import { MapStatus } from "../types/Status.types";
import { preloadConnectionNames } from "../utils/connectionNames";
import { LoginState, useAuth } from "../stores/useAuth";
import { useReownOpenWallet } from "../hooks/useReownOpenWallet";
import { Perf } from "r3f-perf";
import { IsProduction } from "../utils/client";
import Loader from "./ui/Loader";
import { useDevSettings } from "../stores/useDevSettings";

export const SPHERE_RADIUS = 50;

export type MapProgress = {
  isEmpty: boolean;
  progress: number;
}

interface LevelMenuIslandsProps {
  onMapSelect: (mapId: string) => void;
}

export default function LevelMenuIslands({ onMapSelect }: LevelMenuIslandsProps) {
  const { 
    progress, 
    initializeGame, 
    isMapUnlocked,
    isNPCCompleted,
    getMapData,
    getMaps,
    isDataLoaded,
    isProgressLoaded,
    isLoading
  } = useLevelProgress();

  const { loginState } = useAuth();
  const { openWallet } = useReownOpenWallet();
  // Get data from centralized store
  const maps = getMaps();
  const mapData = getMapData();
  const [isPlanetDragging, setIsPlanetDragging] = useState(false);
  const mapIdWaitLogin = useRef<string | undefined>(undefined);
  const [showLoader, setShowLoader] = useState(true);
  const [isCanvasReady, setIsCanvasReady] = useState<boolean>(false);


  const useLocalStorageMode = useDevSettings.getState().useLocalStorage;
  const isWaitingServerData = !useLocalStorageMode && loginState === LoginState.Authentificated && !isProgressLoaded;

  useEffect(() => {
    // Initialize game progress on component mount if not loaded and user is authenticated
    initializeGame();
    // Preload connection names for display
    preloadConnectionNames().catch(console.error);
  }, []);
  
  useEffect(() => {
    const useLocalStorageMode = useDevSettings.getState().useLocalStorage;
    const unsubscribe = useDevSettings.subscribe(
      (state) => {
        if (state.useLocalStorage !== useLocalStorageMode) {
          console.log("🔄 LocalStorage mode changed, reinitializing game...");
          initializeGame();
        }
      }
    );
    
    return () => unsubscribe();
  }, []);

  const handleMapSelect = (mapId: string) => {
    const useLocalStorageMode = useDevSettings.getState().useLocalStorage;
    
    if (isMapUnlocked(mapId)) {
      if (useLocalStorageMode) {
        onMapSelect(mapId);
      } else {
        if (loginState === LoginState.NoUser) {
          mapIdWaitLogin.current = mapId;
          openWallet();
        } else if (loginState === LoginState.Authentificated) {
          onMapSelect(mapId);
        }
      }
    }
  };

  useEffect(() => {
    if (mapIdWaitLogin.current && loginState === LoginState.Authentificated) {
      onMapSelect(mapIdWaitLogin.current);
      mapIdWaitLogin.current = undefined;
    }
  }, [loginState]);

  const getMapStatus = (mapId: string): MapStatus => {
    if (!isMapUnlocked(mapId)) return MapStatus.LOCKED;
    
    // Check if map is completed (all NPCs completed)
    const mapNPCs = progress.filter(item => item.mapId === mapId);
    if (mapNPCs.length > 0) {
      const allCompleted = mapNPCs.every(npc => npc.isCompleted);
      if (allCompleted) return MapStatus.COMPLETED;
    }
    
    return MapStatus.UNLOCKED;
  };

  const getMapProgress = (mapId: string): MapProgress => {
    const mapNPCs = progress.filter(item => item.mapId === mapId);
    if (mapNPCs.length === 0) return {isEmpty: true, progress: 0};
    
    const totalNPCs = mapNPCs.length;
    const completedNPCs = mapNPCs.filter(npc => npc.isCompleted).length;
    
    return {isEmpty: false, progress: (completedNPCs / totalNPCs) * 100};
  };

  
  useEffect(() => {
    const shouldShowLoader = isLoading || isWaitingServerData || !isDataLoaded() || !isCanvasReady;
    
    if (!shouldShowLoader) {
      setTimeout(() => {
        setShowLoader(false);
      }, 500);
    }
  }, [isLoading, isWaitingServerData, isProgressLoaded, isCanvasReady]);

  return (
    <>
      {showLoader && <Loader />}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1000
      }}>
        {/* 3D Canvas */}
        <Canvas
          camera={{
            position: [0, 0, SPHERE_RADIUS * 1.02],
            fov: 38,
            near: 0.01,
            far: 2000,
          }}
          gl={{ logarithmicDepthBuffer: true }}
          style={{ width: '100%', height: '100%' }}
          onCreated={(() => {setIsCanvasReady(true)})}
        >
          {!IsProduction && <Perf position="bottom-left" />}
          <MenuSky />
          
          
          {/* Camera controls - zoom with mouse wheel */}
          <OrbitControls 
            enableZoom={true}
            enablePan={false}
            enableRotate={false}
            minDistance={SPHERE_RADIUS * 0.8}
            maxDistance={SPHERE_RADIUS * 1.5}
            autoRotate={false}
            zoomSpeed={0.5}
          />

          {/* Rotatable Planet */}
          <RotatablePlanet isDragging={isPlanetDragging} setIsDragging={setIsPlanetDragging}>
            {/* Planet sphere (ocean) - Fixed position centered */}
            <mesh position={[0, 0, 0]}>
              <sphereGeometry args={[SPHERE_RADIUS, 36, 36]} />
              <meshStandardMaterial 
                color='#006994'
                roughness={0.1}
                metalness={0.1}
                opacity={0.9}
              />
            </mesh>
            
            {/* Ocean surface with animation */}
            <OceanSphere />

            {/* Connection lines between islands */}
            {Object.keys(mapData).length > 0 && (
              <ConnectionLines 
                maps={maps} 
                mapData={mapData}
                getMapStatus={getMapStatus}
                isNPCCompleted={isNPCCompleted}
                isMapUnlocked={isMapUnlocked}
              />
            )}

            <MapIslands 
              maps={maps}
              progress={progress}
              getMapStatus={getMapStatus}
              getMapProgress={getMapProgress}
              onMapSelect={handleMapSelect}
              isPlanetDragging={isPlanetDragging}
              mapData={mapData}
            />
          </RotatablePlanet>
        </Canvas>

        {/* <MainFooter maps={maps} progress={progress} /> */}
      </div>
    </>
  );
}
